if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
        this.easifyCleanup = null;

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      hide(preventFocus = false) {
        this.teardownEasify();
        this.teardownDeliveryModals();
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        // The preserved Easify help modals get relocated to <body> by ModalDialog's
        // connectedCallback; clear any from a previous quick view so they don't pile up.
        document
          .querySelectorAll('body > #EasifySizeChartModal, body > #EasifyWhatIsThisModal')
          .forEach((modal) => modal.remove());
        this.teardownDeliveryModals();

        const productUrl = opener.getAttribute('data-product-url');

        fetch(productUrl)
          .then((response) => response.text())
          .then(async (responseText) => {
            await this.ensureEasifyReady();

            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            this.ensureDeliveryDateStyles(responseHTML);
            const productElement = responseHTML.querySelector('product-info');

            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            super.show(opener);

            const productInfo = this.modalContent.querySelector('product-info');

            if (window.EasifyHelpPopups) {
              window.EasifyHelpPopups.observe(productInfo);
            }

            // Gate the add-to-cart button until Easify options load in the quick view.
            if (window.EasifyAddToCartGate) {
              window.EasifyAddToCartGate.gate(productInfo);
            }

            if (window.DeliveryDate) {
              window.DeliveryDate.refresh();
            }

            await this.renderEasifyOptions(productInfo, productUrl);
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      ensureDeliveryDateStyles(responseHTML) {
        if (document.querySelector('link[href*="component-delivery-date.css"]')) return;

        const srcLink = responseHTML?.querySelector('link[href*="component-delivery-date.css"]');
        const href = srcLink?.getAttribute('href');
        if (!href) return;

        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = href;
        document.head.appendChild(link);
      }

      teardownDeliveryModals() {
        document.querySelectorAll('body > [data-delivery-modal]').forEach((modal) => {
          const ownerId = modal.dataset.deliveryModalOwner;
          if (ownerId && ownerId.includes('quickadd-')) {
            modal.remove();
            return;
          }
          // Also remove orphans whose owner container no longer exists.
          if (ownerId && !document.getElementById(ownerId)) {
            modal.remove();
          }
        });
      }

      ensureEasifyReady() {
        if (typeof window.loadDeferredEasifyScripts === 'function') {
          window.loadDeferredEasifyScripts();
        }

        if (typeof window.TPORenderOptionSetQuickView === 'function') {
          this.patchEasifyQuickViewFormFinder();
          return Promise.resolve();
        }

        return new Promise((resolve) => {
          const startedAt = Date.now();
          const maxWaitMs = 8000;
          const tick = () => {
            this.loadEasifyQuickViewModule();
            if (typeof window.TPORenderOptionSetQuickView === 'function') {
              this.patchEasifyQuickViewFormFinder();
              resolve();
              return;
            }
            if (Date.now() - startedAt >= maxWaitMs) {
              resolve();
              return;
            }
            setTimeout(tick, 100);
          };
          tick();
        });
      }

      // Easify only loads its quick view module (which defines TPORenderOptionSetQuickView)
      // on non-product templates. On the PDP (e.g. recently viewed quick add) import it
      // directly: find the lazy chunk referenced by the main Easify script that defines it.
      loadEasifyQuickViewModule() {
        if (QuickAddModal.easifyQuickViewModule) return;
        if (!window.TPOConfigs?.template?.includes('product')) return;

        const mainScript = Array.from(document.querySelectorAll('script[src*="easify-product-options"]')).find(
          (script) => /\/product-options-[\w-]+\.js/.test(script.src)
        );
        if (!mainScript) return;

        const mainUrl = new URL(mainScript.src, window.location.href);
        QuickAddModal.easifyQuickViewModule = fetch(mainUrl)
          .then((response) => response.text())
          .then(async (source) => {
            const chunks = [...new Set(Array.from(source.matchAll(/import\("\.\/(index-[\w-]+\.js)"\)/g), (m) => m[1]))];
            for (const chunk of chunks) {
              const chunkUrl = new URL(chunk, mainUrl).href;
              const chunkSource = await fetch(chunkUrl).then((response) => response.text());
              if (chunkSource.includes('TPORenderOptionSetQuickView')) {
                await import(chunkUrl);
                return;
              }
            }
          })
          .catch((error) => {
            console.warn('[quick-add] Unable to load Easify quick view module', error);
          });
      }

      patchEasifyQuickViewFormFinder() {
        window.TPOCustomFunctions = window.TPOCustomFunctions || {};
        window.TPOCustomFunctions.quickView = Object.assign({}, window.TPOCustomFunctions.quickView, {
          getProductFormContainer(root = document) {
            if (!root || !root.querySelector) return null;
            return (
              root.querySelector('product-form form.form[action*="/cart/add"]') ||
              root.querySelector('product-form form[action*="/cart/add"]') ||
              root.querySelector('form.form:has(.product-form__buttons)') ||
              root.querySelector('form:has(.product-form__buttons)')
            );
          },
        });
      }

      async renderEasifyOptions(productInfo, productUrl) {
        if (!productInfo || typeof window.TPORenderOptionSetQuickView !== 'function') return;

        const form =
          productInfo.querySelector('product-form form.form[action*="/cart/add"]') ||
          productInfo.querySelector('product-form form[action*="/cart/add"]');
        if (!form) return;

        let product = this.readEasifyProductPayload(productInfo);

        if (!product) {
          const handle = (productUrl || '').split('/products/')[1]?.split(/[?#]/)[0];
          if (!handle) return;

          try {
            product = await fetch(`/products/${handle}.js`).then((response) => {
              if (!response.ok) throw new Error(`product.js ${response.status}`);
              return response.json();
            });
          } catch (error) {
            console.warn('[quick-add] Unable to load product for Easify options', error);
            return;
          }
        }

        product = this.normalizeEasifyProduct(product);

        const variantId = String(form.querySelector('[name="id"]')?.value || '');
        const selectedVariant =
          (product.variants || []).find((variant) => String(variant.id) === variantId) ||
          (product.variants || [])[0];

        if (!selectedVariant) return;

        this.teardownEasify();

        let mount = this.querySelector('.easify-product-options');
        if (!mount) {
          mount = document.createElement('div');
          mount.className = 'easify easify-product-options';
          form.parentElement?.insertBefore(mount, form);
        }

        try {
          this.easifyCleanup = window.TPORenderOptionSetQuickView(
            mount,
            this,
            product,
            selectedVariant
          );
        } catch (error) {
          console.warn('[quick-add] Easify quick view render failed', error);
        }
      }

      readEasifyProductPayload(productInfo) {
        const script = productInfo.querySelector('script[data-easify-quickview-product]');
        if (!script?.textContent?.trim()) return null;

        try {
          return JSON.parse(script.textContent);
        } catch (error) {
          console.warn('[quick-add] Invalid Easify product payload', error);
          return null;
        }
      }

      normalizeEasifyProduct(product) {
        const clone = JSON.parse(JSON.stringify(product));
        const stringifyId = (value) => (value == null ? value : String(value));
        clone.id = stringifyId(clone.id);
        if (Array.isArray(clone.variants)) {
          clone.variants = clone.variants.map((variant) => ({
            ...variant,
            id: stringifyId(variant.id),
            product_id: stringifyId(variant.product_id || clone.id),
          }));
        }
        return clone;
      }

      teardownEasify() {
        if (typeof this.easifyCleanup === 'function') {
          try {
            this.easifyCleanup();
          } catch (error) {
            // Ignore unmount races when the modal content was already cleared.
          }
        }
        this.easifyCleanup = null;
        if (this.__tpoRenderData) delete this.__tpoRenderData;
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        productElement.querySelectorAll('form.installment').forEach((form) => form.remove());

        // Preserve the Easify "Size chart" / "What is this?" help modals so their
        // links remain clickable inside the quick view popup; strip the rest.
        const modalDialog = productElement.querySelectorAll(
          'modal-dialog:not(#EasifySizeChartModal):not(#EasifyWhatIsThisModal)'
        );
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}
